import moment from 'moment'
import React, { useCallback, useMemo } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import styled from 'styled-components'

import FlightLookupListing from './FlightLookupListing'
import {
  flightCarrierCodeConfig,
  formOptions,
} from '../../Common/Fields/config'
import RHFDatePickerInput from '../../Common/Fields/RHFDatePickerInput'
import Input from '../../Common/Fields/RHFInput'
import CreateFlight from '../../Create/CreateFlight'

import { addToTripClickEvent } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import Group from 'components/utils/Group'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'

interface Props {
  tripId: string
}

const UppercaseInput = styled(Input)`
  input {
    text-transform: uppercase;
  }
`

export default function FlightLookupForm({ tripId }: Props) {
  const { closeModal, goBack } = useModalCallbacks()
  const openModal = useOpenModal()

  const methods = useForm<{
    flightCode: string
    departureDate: string
  }>({
    ...formOptions,
    shouldUnregister: true,
    defaultValues: {
      departureDate: undefined,
      flightCode: '',
    },
  })
  const { register, formState, getValues, handleSubmit } = methods

  const onFindFlightClick = useCallback(() => {
    fireInteractionEvent(addToTripClickEvent('modal_flights_custom_lookup'))
    openModal(FlightLookupListing, {
      tripId,
      flightCode: getValues('flightCode').toUpperCase(),
      departureDate: moment(getValues('departureDate')),
    })
  }, [openModal, getValues, tripId])

  const today = moment()
  const [minDate, maxDate] = useMemo(() => {
    return [
      today.clone().subtract(1, 'days').toDate(),
      today.clone().add(12, 'months').toDate(),
    ]
    // Disable exhaustive-deps because we only want to recalculate min/max date
    // if the actual date has changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [today.format(ISO_DATE_FORMAT)])

  return (
    <>
      <ModalHeader
        title="Flight"
        onBackButtonClick={goBack}
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <FormProvider {...methods}>
          <ModalContent>
            <Group
              direction="horizontal"
              horizontalAlign="stretch"
              gap={16}
              verticalAlign="start"
            >
              <UppercaseInput
                {...register('flightCode', {
                  ...flightCarrierCodeConfig,
                  setValueAs: (val: string) => val.toUpperCase(),
                })}
                required
                label="Flight number"
                placeholder="e.g. QF1"
              />
              {/* @ts-ignore default values for props of class component */}
              <RHFDatePickerInput
                name="departureDate"
                label="Departure day"
                min={minDate}
                max={maxDate}
                required
              />
            </Group>
            <VerticalSpacer gap={16}>
              <TextButton
                kind="primary"
                size="large"
                fit="full-width"
                onClick={handleSubmit(onFindFlightClick)}
                disabled={!formState.isValid}
              >
                Find my flight
              </TextButton>
              <TextButton
                kind="tertiary"
                size="large"
                fit="full-width"
                onClick={() => {
                  fireInteractionEvent(
                    addToTripClickEvent(
                      'modal_flights_custom_manual',
                      'prelookup',
                    ),
                  )
                  openModal(CreateFlight, { tripId })
                }}
              >
                or enter details manually
              </TextButton>
            </VerticalSpacer>
          </ModalContent>
        </FormProvider>
      </ModalBody>
    </>
  )
}
